import { Suspense, useEffect, useState, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
const MyDishes = lazy(() => import("./pages/MyDishes.tsx"));
const DishDetail = lazy(() => import("./pages/DishDetail.tsx"));
const MenuView = lazy(() => import("./pages/MenuView.tsx"));
const Stats = lazy(() => import("./pages/Stats.tsx"));
const FoodMap = lazy(() => import("./pages/FoodMap.tsx"));
const GroupDining = lazy(() => import("./pages/GroupDining.tsx"));
const TasteProfile = lazy(() => import("./pages/TasteProfile.tsx"));
import NotFound from "./pages/NotFound.tsx";
const SharedMenu = lazy(() => import("./pages/SharedMenu.tsx"));
const SharedWrapped = lazy(() => import("./pages/SharedWrapped.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const Notifications = lazy(() => import("./pages/Notifications.tsx"));
const Leaderboard = lazy(() => import("./pages/Leaderboard.tsx"));
import ScrollToTop from "./components/ScrollToTop.tsx";
import NotificationManager from "./components/NotificationManager.tsx";
import ErrorBoundary from "./components/ErrorBoundary";
import { track } from "@/lib/analytics";
// refreshDishImages moved to lazy load to avoid TDZ crash

export interface Dish {
  id: string;
  name: string;
  description?: string;
  restaurantName?: string;
  rating?: number;
  imageUrl?: string;
  tags?: string[];
  calories?: number;
  createdAt: string;
  imageGenerationId?: string;
}

export interface DishesContextType {
  dishes: Dish[];
  setDishes: React.Dispatch<React.SetStateAction<Dish[]>>;
}

import { createContext, useContext } from "react";
export const DishesContext = createContext<DishesContextType>({
  dishes: [],
  setDishes: () => {},
});

const queryClient = new QueryClient();

const App = () => {
  const [dishes, setDishes] = useState<Dish[]>([]);

  useEffect(() => {
    track("app_opened");
    // Dismiss the inline boot splash from index.html as soon as the React tree
    // has committed its first render (this useEffect fires post-paint).
    document.dispatchEvent(new Event("yumz:ready"));
    void import("./lib/storage").then(m => m.refreshDishImages());
    void import("./lib/notifications").then(m => {
      m.clearBadge();
      // initNotifications self-gates on Capacitor.isNativePlatform; calling it
      // here (post-paint) defers the iOS permission prompt out of the cold-start path.
      m.initNotifications();
    });
  }, []);

  return (
    <DishesContext.Provider value={{ dishes, setDishes }}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter basename={window.location.pathname.startsWith('/app') ? '/app' : '/'}>
            <ScrollToTop />
            <NotificationManager />
            <Routes>
              <Route path="/" element={
                <ErrorBoundary>
                  <Index />
                </ErrorBoundary>
              } />
              <Route path="/my-dishes" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><MyDishes /></Suspense>
                </ErrorBoundary>
              } />
              <Route
                path="/dish/:id"
                element={
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>
                      }
                    >
                      <DishDetail />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route path="/menu/:id" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><MenuView /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/stats" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><Stats /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/food-map" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><FoodMap /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/group" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><GroupDining /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/taste-profile" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><TasteProfile /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/shared/:shareCode" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><SharedMenu /></Suspense>
                </ErrorBoundary>
              } />
              {/* Wave I — Yumz Wrapped recipient page. /w/<code> opens the
                  recipient's view of a sender's wrapped (map + stats + react). */}
              <Route path="/w/:shareCode" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><SharedWrapped /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/admin" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"black",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #333",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><Admin /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/leaderboard" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTopColor:"#a855f7",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><Leaderboard /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="/notifications" element={
                <ErrorBoundary>
                  <Suspense fallback={<div style={{minHeight:"100vh",background:"white",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:32,height:32,border:"3px solid #eee",borderTopColor:"#333",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} /></div>}><Notifications /></Suspense>
                </ErrorBoundary>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </DishesContext.Provider>
  );
};

export default App;
